/**
 * Voluum Flow — typed API client (Standard tab v2 + Snapshots)
 * For Media Devoted | Created By Ciro Autiero
 */

import api from '@/services/api';
import { stripTrafficPulsePathAiFromFlow } from '@/features/flow-optimizer/tabs/standard/v2/utils/path-ai';

import {
  normalizeVoluumFlowPathsForPut,
  validateFlowUpdateOrThrow,
  type TrackerId,
  type VoluumFlowResource,
  type VoluumFlowSnapshot,
  type VoluumFlowUpdate,
} from '@trafficpulse/shared';

const FLOW_BASE = '/voluum/flows';

/** Snapshots live under /{tracker}/flows/:id/snapshots for every tracker. */
const snapshotsBase = (provider: TrackerId): string =>
  `/${provider}/flows`;

export async function getRawFlow(flowId: string): Promise<VoluumFlowResource> {
  const { data } = await api.get(`${FLOW_BASE}/${flowId}/raw`);
  return (data.data ?? data) as VoluumFlowResource;
}

/** The PUT body every raw-flow write sends (Voluum, and Tres Commas' twin). */
export function prepareRawFlowPut(body: VoluumFlowUpdate): VoluumFlowUpdate {
  const payload = stripTrafficPulsePathAiFromFlow(body);
  normalizeVoluumFlowPathsForPut(payload);
  // Throws `VoluumPayloadValidationError` with field paths when the body
  // doesn't conform to `flow_FlowUpdateResource`. The mutation hook surfaces
  // it through the toast; nothing reaches the network on failure.
  validateFlowUpdateOrThrow(payload);
  return payload;
}

export async function updateRawFlow(
  flowId: string,
  body: VoluumFlowUpdate,
): Promise<{ success: boolean; flow: VoluumFlowResource }> {
  const payload = prepareRawFlowPut(body);
  const { data } = await api.put(`${FLOW_BASE}/${flowId}/raw`, payload);
  return (data.data ?? data) as { success: boolean; flow: VoluumFlowResource };
}

export async function listFlowSnapshots(
  flowId: string,
  provider: TrackerId = 'voluum',
): Promise<{ snapshots: VoluumFlowSnapshot[]; count: number }> {
  const { data } = await api.get(`${snapshotsBase(provider)}/${flowId}/snapshots`);
  return (data.data ?? data) as {
    snapshots: VoluumFlowSnapshot[];
    count: number;
  };
}

export async function takeFlowSnapshot(
  flowId: string,
  body?: { label?: string },
  provider: TrackerId = 'voluum',
): Promise<VoluumFlowSnapshot> {
  const { data } = await api.post(
    `${snapshotsBase(provider)}/${flowId}/snapshots`,
    body ?? {},
  );
  return (data.data ?? data) as VoluumFlowSnapshot;
}

export async function restoreFlowSnapshot(
  flowId: string,
  snapshotId: string,
  provider: TrackerId = 'voluum',
  /** Also sync the bank/queue lifecycle to the restored flow (recommended). */
  syncQueue = true,
): Promise<VoluumFlowSnapshot> {
  const { data } = await api.post(
    `${snapshotsBase(provider)}/${flowId}/snapshots/${snapshotId}/restore`,
    { syncQueue },
  );
  return (data.data ?? data) as VoluumFlowSnapshot;
}

export async function deleteFlowSnapshot(
  flowId: string,
  snapshotId: string,
  provider: TrackerId = 'voluum',
): Promise<void> {
  await api.delete(`${snapshotsBase(provider)}/${flowId}/snapshots/${snapshotId}`);
}
